import { notifyFeedbackActionCompleted } from '../utils/feedbackEvents';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ivrService from '../services/ivrService';
import clinicService from '../services/clinicService';
import distributorService from '../services/distributorService';
import ocrService from '../services/ocrService';
import api from '../services/api';
import { uploadDocuments } from '../utils/uploadDocuments';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/roles';
import { isValidMbi } from '../utils/mbi';
import { requestErrorMessage } from '../utils/requestError';
import { accountId, captureSession, isSessionCurrent } from '../utils/authSession';
import { ivrSubmissionAttempt } from '../utils/ivrSubmissionAttempt';
import { useT } from '../i18n/LanguageContext';
import { buildIvrPayload } from '../features/ivr/buildIvrPayload';
import useAccessibleDialog from '../hooks/useAccessibleDialog';
import useLiveIvrCapability from '../hooks/useLiveIvrCapability';
import useLiveIvrAcknowledgment from '../hooks/useLiveIvrAcknowledgment';
import IvrSubmissionFooter from '../features/ivr/IvrSubmissionFooter';
import IvrClinicSelection from '../features/ivr/IvrClinicSelection';
import LocalDemoIvrPanel from '../features/ivr/LocalDemoIvrPanel';
import {
  localDemoEnvironment,
  DEMO_IVR_PATIENT,
  truthfulDemoIvrReceipt,
} from '../utils/localDemoEnvironment';
import { CARD_SCAN_MESSAGES } from '../features/ivr/cardScanMessages';
import { shippingAccessScope } from '../hooks/useShippingRequest';

const inputClass =
  'w-full h-[40px] border border-[#d6dce8] rounded-[8px] px-3 text-[13px] text-[#0f172a] placeholder:text-[#97a3b6] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]';

export default function NewIVRModal({
  open,
  onClose,
  onCreated,
  distributorClinicId,
  readOnly = false,
}) {
  const t = useT();
  const { user, sessionVersion } = useAuth();
  const actorId = accountId(user);
  const admin = isAdmin(user);
  const master = user?.role === 'master_distributor';
  const viewOnly = readOnly || Boolean(user?.distributorPreviewExpiresAt);
  const scope = shippingAccessScope();
  const target = `${scope}:${master ? distributorClinicId || '' : ''}:${viewOnly}`;
  const targetRef = useRef(target);
  targetRef.current = target;
  const distributorClinic = user?.role === 'clinic' && Boolean(user?.distributor);
  const ownClinicId = user?.clinic?._id || user?.clinic;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [medicareId, setMedicareId] = useState('');
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [clinic, setClinic] = useState('');
  const [clinicLoad, setClinicLoad] = useState('loading');
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const liveIvr = useLiveIvrCapability({ open, actorId, sessionVersion, target });
  const liveAdmin = liveIvr.required && admin;
  const localDemo = !liveIvr.required && localDemoEnvironment();
  const [liveConfirmed, setLiveConfirmed] = useLiveIvrAcknowledgment(
    `${actorId}:${sessionVersion}:${target}:${open}`,
    { firstName, lastName, medicareId, comment, clinic, files }
  );
  const [scanNote, setScanNote] = useState(null);
  const reset = useCallback(() => {
    setLiveConfirmed(false);
    setFirstName('');
    setLastName('');
    setMedicareId('');
    setComment('');
    setFiles([]);
    setClinic('');
    setClinicLoad('loading');
    setScanNote(null);
    setScanning(false);
  }, [setLiveConfirmed]);
  useEffect(() => {
    if (!open && liveIvr.required) reset();
  }, [open, liveIvr.required, reset]);
  const fileInputRef = useRef(null);
  const cardInputRef = useRef(null);
  const submissionRef = useRef(null);
  // React state does not update synchronously between two clicks in the same event batch.
  const flightRef = useRef(null);
  const scanFlightRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      submissionRef.current = null;
      flightRef.current = null;
      scanFlightRef.current = null;
    };
  }, []);
  useEffect(() => {
    submissionRef.current = null;
    flightRef.current = null;
    scanFlightRef.current = null;
    setSubmitting(false);
    reset();
    setClinics([]);
  }, [actorId, sessionVersion, target, reset]);
  useEffect(() => {
    if (!open) return;
    let active = true;
    const session = captureSession();
    const current = () =>
      active &&
      mountedRef.current &&
      targetRef.current === target &&
      scope === shippingAccessScope() &&
      isSessionCurrent(session);
    setClinic('');
    setClinics([]);
    setClinicLoad('loading');
    if (liveAdmin) {
      setClinicLoad('loaded');
      return;
    }
    const source = master
      ? distributorService.listClinics({ sessionBinding: session })
      : clinicService.getClinics();
    source
      .then((res) => {
        if (!current()) return;
        const data = res.data?.data?.data || res.data?.data || [];
        let list = Array.isArray(data) ? data : [];
        if (master) {
          list = list.filter((c) => c?._id === distributorClinicId && typeof c.name === 'string');
          if (!distributorClinicId || list.length !== 1)
            throw new Error('Customer selection unavailable');
        }
        if (
          distributorClinic &&
          (list.length !== 1 || !ownClinicId || list[0]?._id !== ownClinicId)
        ) {
          throw new Error('Clinic selection unavailable');
        }
        setClinics(list);
        setClinicLoad('loaded');
        if (list.length === 1) setClinic(list[0]._id);
      })
      .catch(() => {
        if (current()) {
          setClinics([]);
          setClinicLoad('error');
        }
      });
    return () => {
      active = false;
    };
  }, [
    open,
    actorId,
    sessionVersion,
    liveAdmin,
    distributorClinic,
    ownClinicId,
    master,
    distributorClinicId,
    scope,
    target,
  ]);
  const busy = submitting || scanning;
  const close = () => {
    if (busy || flightRef.current || scanFlightRef.current) return;
    reset();
    onClose();
  };
  const { dialogRef, titleId } = useAccessibleDialog({
    open,
    onClose: close,
    closeDisabled: busy,
  });
  if (!open) return null;
  const emptyOkForAdmin = admin && clinicLoad === 'loaded' && clinics.length === 0;
  const repHasNoClinics = !admin && clinicLoad === 'loaded' && clinics.length === 0;
  const clinicSatisfied = liveAdmin ? liveIvr.ready : !!clinic || emptyOkForAdmin;
  const canSubmit =
    !!firstName.trim() &&
    !!lastName.trim() &&
    isValidMbi(medicareId) &&
    clinicSatisfied &&
    !busy &&
    !viewOnly &&
    (!liveIvr.required || (liveIvr.ready && admin && liveConfirmed));
  const handleScanCard = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = '';
    if (!file || viewOnly || busy || flightRef.current || scanFlightRef.current) return;
    if (scope !== shippingAccessScope()) return;
    const session = captureSession();
    if (session.userId !== actorId) return;
    const reservation = {};
    scanFlightRef.current = reservation;
    const current = () =>
      mountedRef.current &&
      scanFlightRef.current === reservation &&
      targetRef.current === target &&
      scope === shippingAccessScope() &&
      isSessionCurrent(session);
    setFirstName('');
    setLastName('');
    setMedicareId('');
    setScanning(true);
    setScanNote(null);
    try {
      const res = await ocrService.scanMedicareCard(file, { sessionBinding: session });
      if (!current()) return;
      const data = res.data?.data || res.data || {};
      if (data.needsManualEntry) {
        setFirstName('');
        setLastName('');
        setMedicareId('');
        setScanNote({
          type: 'warn',
          text:
            CARD_SCAN_MESSAGES.find((message) => message === data.message) ||
            'Could not read the card. Please enter the details manually.',
        });
        return;
      }
      const f = data.fields || {};
      setFirstName(f.firstName || '');
      setLastName(f.lastName || '');
      setMedicareId(f.mbi || '');
      setScanNote({
        type: data.message ? 'warn' : 'ok',
        text: data.message
          ? '{message} Please verify the fields below.'
          : 'Scanned - Please verify the fields below.',
        message: data.message
          ? CARD_SCAN_MESSAGES.find((message) => message === data.message) ||
            'Could not read the card. Please enter the details manually.'
          : undefined,
      });
    } catch (err) {
      if (current())
        setScanNote({
          type: 'warn',
          text: 'Card scan failed. Please enter the details manually.',
        });
    } finally {
      if (scanFlightRef.current === reservation) {
        scanFlightRef.current = null;
        if (mountedRef.current && isSessionCurrent(session)) setScanning(false);
      }
    }
  };
  const handleAddFiles = (e) => {
    if (viewOnly || busy) return;
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };
  const handleSubmit = async () => {
    if (!canSubmit || flightRef.current || scanFlightRef.current || scope !== shippingAccessScope())
      return;
    if (liveIvr.required && (!liveIvr.isCurrent() || !admin || !liveConfirmed)) return;
    const session = captureSession();
    if (session.userId !== actorId) return;
    const liveIvrConfirmed = liveIvr.required && liveIvr.ready && admin && liveConfirmed;
    setLiveConfirmed(false);
    const reservation = {};
    flightRef.current = reservation;
    setSubmitting(true);
    let attempt;
    let unconfirmedReceipt = false;
    const current = () =>
      mountedRef.current &&
      flightRef.current === reservation &&
      targetRef.current === target &&
      scope === shippingAccessScope() &&
      isSessionCurrent(session);
    try {
      attempt = ivrSubmissionAttempt(
        submissionRef.current,
        session,
        buildIvrPayload({
          firstName,
          lastName,
          medicareId,
          comment,
          clinic: liveAdmin ? '' : clinic,
        }),
        files
      );
      submissionRef.current = attempt;
      const { urls: uploadedDocs, failure: uploadFailure } = await uploadDocuments(
        attempt.files,
        async (file) => {
          if (!current()) throw new Error('Your session changed.');
          if (attempt.uploads.has(file)) return { data: { url: attempt.uploads.get(file) } };
          const formData = new FormData();
          formData.append('file', file);
          const response = await api.post('/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            sessionBinding: session,
          });
          if (!current()) throw new Error('Your session changed.');
          const url =
            response?.data?.data?.url || response?.data?.data?.filePath || response?.data?.url;
          if (url) attempt.uploads.set(file, url);
          return response;
        }
      );
      if (!current()) return;
      if (uploadFailure) {
        alert(
          t(
            'A document failed to upload, so nothing has been submitted. Your attachments are still attached - please try again.'
          )
        );
        return;
      }
      // An unknown POST result reuses these exact uploaded URLs and the same key on retry.
      const payload = {
        ...attempt.payload,
        ...(uploadedDocs.length ? { documents: uploadedDocs } : {}),
      };
      const response = await ivrService.submitIVR(payload, {
        idempotencyKey: attempt.key,
        sessionBinding: session,
        ...(liveIvrConfirmed ? { liveIvrConfirmed: true } : {}),
      });
      if (!current()) return;
      const receipt = response?.data?.data;
      if (localDemo && !truthfulDemoIvrReceipt(receipt)) {
        unconfirmedReceipt = true;
        throw new Error('Unconfirmed demo receipt');
      }
      const receiptClinic = receipt?.clinic?._id || receipt?.clinic;
      const confirmed =
        typeof receipt?._id === 'string' &&
        /^[a-f0-9]{24}$/i.test(receipt?._id) &&
        receiptClinic === clinic &&
        receipt.businessContext === 'distributor' &&
        (receipt.distributorId?._id || receipt.distributorId) ===
          (user?.distributor?._id || user?.distributor);
      if (master && !confirmed) {
        unconfirmedReceipt = true;
        throw new Error('Unconfirmed IVR response');
      }
      submissionRef.current = null;
      if (localDemo) alert(t('Demo request saved. No insurance check was performed.'));
      reset();
      onCreated();
      onClose();
      notifyFeedbackActionCompleted();
    } catch (err) {
      if (!current()) return;
      if (
        err?.response?.status === 400 &&
        err?.response?.data?.code === 'IVR_ATTACHMENT_REUPLOAD_REQUIRED'
      ) {
        // Only explicit attachment rejection resets the retry identity.
        submissionRef.current = null;
      }
      const fallback = unconfirmedReceipt
        ? 'The IVR result could not be confirmed. Check the IVR list or retry this unchanged request.'
        : err?.response?.status === 409
          ? 'This submission could not be matched to your previous attempt. Check the IVR list before submitting again.'
          : 'Failed to submit IVR request';
      alert(t(requestErrorMessage(err, fallback)));
    } finally {
      if (flightRef.current === reservation) {
        flightRef.current = null;
        if (mountedRef.current && isSessionCurrent(session)) setSubmitting(false);
      }
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={close} data-testid="modal-backdrop" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative bg-white rounded-[14px] shadow-xl w-[500px] max-w-[calc(100vw-2rem)] z-10 max-h-[90vh] overflow-y-auto"
      >
        <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px] px-5 py-4 flex items-center justify-between">
          <h2 id={titleId} className="text-[16px] font-semibold text-[#0f172a]">
            {t('New IVR Request')}
          </h2>
          <button
            onClick={close}
            aria-label={t('Close')}
            className="w-6 h-6 flex items-center justify-center text-[#64748b] hover:text-[#0f172a]"
          >
            <span className="material-symbols-outlined text-[24px]">cancel</span>
          </button>
        </div>
        <div className="p-5">
          {localDemo && (
            <LocalDemoIvrPanel
              t={t}
              disabled={busy || viewOnly}
              onExample={() => {
                setFirstName(DEMO_IVR_PATIENT.firstName);
                setLastName(DEMO_IVR_PATIENT.lastName);
                setMedicareId(DEMO_IVR_PATIENT.medicareId);
              }}
            />
          )}
          <div className="mb-4">
            <input
              ref={cardInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleScanCard}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => cardInputRef.current?.click()}
              disabled={busy || viewOnly}
              className="w-full h-[40px] border border-[#0089ff] rounded-[8px] bg-[#f0f7ff] flex items-center justify-center gap-2 hover:bg-[#e6f1ff] transition-colors disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-[#0089ff] text-[18px]">
                photo_camera
              </span>
              <span className="text-[13px] font-medium text-[#0089ff]">
                {scanning ? t('Scanning card...') : t('Scan Medicare card')}
              </span>
            </button>
            {scanNote && (
              <div
                role="status"
                aria-live="polite"
                className={`mt-2 px-3 py-2 rounded-[8px] text-[12px] ${
                  scanNote.type === 'ok'
                    ? 'bg-[rgba(222,252,237,0.6)] text-[#007a55]'
                    : 'bg-[rgba(254,243,199,0.7)] text-[#92660a]'
                }`}
              >
                {t(scanNote.text, scanNote.message ? { message: t(scanNote.message) } : undefined)}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label
                htmlFor="new-ivr-first-name"
                className="block text-[12px] font-medium text-[#0f172a] mb-1"
              >
                {t('First Name')} <span className="text-[#f23e41]">*</span>
              </label>
              <input
                id="new-ivr-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={t('Patient first name')}
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="new-ivr-last-name"
                className="block text-[12px] font-medium text-[#0f172a] mb-1"
              >
                {t('Last Name')} <span className="text-[#f23e41]">*</span>
              </label>
              <input
                id="new-ivr-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={t('Patient last name')}
                className={inputClass}
              />
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor="new-ivr-medicare-id"
              className="block text-[12px] font-medium text-[#0f172a] mb-1"
            >
              {t('Medicare ID')} <span className="text-[#f23e41]">*</span>
            </label>
            <input
              id="new-ivr-medicare-id"
              value={medicareId}
              onChange={(e) => setMedicareId(e.target.value)}
              placeholder={t('11-character MBI')}
              className={inputClass}
            />
            {medicareId.trim() && !isValidMbi(medicareId) && (
              <p className="text-[11px] text-[#92660a] mt-1">
                {t(
                  'Enter the 11-character Medicare ID exactly as shown on the card. Check the letters and numbers.'
                )}
              </p>
            )}
          </div>
          <IvrClinicSelection
            {...{ liveAdmin, clinicLoad, master, repHasNoClinics, clinics, clinic, setClinic, t }}
          />
          <div className="mb-4">
            <label
              htmlFor="new-ivr-comment"
              className="block text-[12px] font-medium text-[#0f172a] mb-1"
            >
              {t('Comment')}
            </label>
            <textarea
              id="new-ivr-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('Additional context for this request...')}
              className="w-full h-[70px] border border-[#d6dce8] rounded-[8px] px-3 py-2 text-[13px] text-[#0f172a] placeholder:text-[#97a3b6] resize-none focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
            />
          </div>
          <div className="mb-5">
            <p className="text-[12px] font-medium text-[#0f172a] mb-1">{t('Documents')}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={handleAddFiles}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={viewOnly || busy}
              className="w-full h-[40px] border border-dashed border-[#d6dce8] rounded-[8px] bg-white flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
            >
              <span className="material-symbols-outlined text-[#64748b] text-[18px]">
                attach_file
              </span>
              <span className="text-[13px] font-medium text-[#64748b]">
                {t('Attach Documents (optional)')}
              </span>
            </button>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between text-[12px] text-[#0f172a] border border-[#e2e8f0] rounded-[6px] px-2 py-1"
                  >
                    <span className="truncate max-w-[360px]">{f.name}</span>
                    <button
                      onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-[#64748b] hover:text-[#f23e41]"
                      aria-label={t('Remove {name}', { name: f.name })}
                    >
                      <span className="material-symbols-outlined text-[14px]">close</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <IvrSubmissionFooter
            {...{
              liveIvr,
              admin,
              liveConfirmed,
              setLiveConfirmed,
              busy,
              viewOnly,
              close,
              handleSubmit,
              canSubmit,
              submitting,
              t,
            }}
          />
        </div>
      </div>
    </div>
  );
}
